import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, Flex, SimpleGrid, useColorModeValue, Spinner } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import CryptoWalletsTable from "views/admin/dataTables/components/CryptoWalletsTable";
import CryptoBalancesCard from "components/card/CryptoBalancesCard";
import CryptoReceiveModal from "components/card/CryptoReceiveModal";
import useEndpoints from "providers/Endpoints";
import constants from "providers/Constants";
import { useSearch } from "contexts/SearchContext";

export default function Wallets() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const endpoints = useEndpoints();
  const { searchQuery } = useSearch();

  const [fiatWallets, setFiatWallets] = useState([]);
  const [cryptoBalances, setCryptoBalances] = useState([]);
  const [cryptoWallets, setCryptoWallets] = useState([]);
  const [selectedCryptoBalance, setSelectedCryptoBalance] = useState({});
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingWallet, setCreatingWallet] = useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [receiveDetails, setReceiveDetails] = useState(null);
  const [loadingReceiveDetails, setLoadingReceiveDetails] = useState(false);

  // Track URL params for restoring selection
  const initialNetwork = useRef(searchParams.get('network'));
  const initialAddress = useRef(searchParams.get('address'));

  const textColor = useColorModeValue("secondaryGray.900", "white");

  useEffect(() => {
    getNetworks();
  }, []);

  const handleControlClick = useCallback((action) => {
    // Additional control actions can be handled here if needed
  }, []);

  const handleBalanceChange = useCallback((balance) => {
    setSelectedCryptoBalance(balance);
    if (balance?.network && balance?.address) {
      setSearchParams({ network: balance.network, address: balance.address }, { replace: true });
    }
    getCryptoWallets(balance?.network);
  }, [setSearchParams]);

  const handleReceiveClick = useCallback(async () => {
    if (!selectedCryptoBalance?.address || !selectedCryptoBalance?.network) {
      constants.showToast("error", t("FAILED"), t("Please select a wallet first"));
      return;
    }
    getCryptoReceiveDetails(selectedCryptoBalance.network, selectedCryptoBalance.address);
  }, [selectedCryptoBalance, t]);

  const handleCreateWallet = useCallback((postData, onSuccess) => {
    createWallet(postData, onSuccess);
  }, []);

  const handleViewTransactions = useCallback((network, address) => {
    navigate(`/admin/crypto-transactions/${network}/${address}`);
  }, [navigate]);

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {!loading && (
        <SimpleGrid mb="20px" columns={{ sm: 1, md: 1, lg: 1, "2xl": 1 }} gap='20px'>
          <CryptoBalancesCard
            networks={networks}
            controls={true}
            isCreatingWallet={creatingWallet}
            onControlClick={handleControlClick}
            onBalanceChange={handleBalanceChange}
            onReceiveClick={handleReceiveClick}
            onViewTransactions={handleViewTransactions}
            name={t("Your Crypto Balance")}
            value={`${selectedCryptoBalance?.symbol || t("N/A")} ${Number(selectedCryptoBalance?.available_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`}
            cryptoBalances={cryptoBalances}
            selectedCryptoBalance={selectedCryptoBalance}
            onCreateWallet={handleCreateWallet}
            fiatWallets={fiatWallets}
            onGetConversionRates={getConversionRates}
            onGetNetworkFees={getNetworkFees}
            onBuyCrypto={buyCrypto}
          />
        </SimpleGrid>
      )}

      <CryptoReceiveModal
        isOpen={receiveModalOpen}
        onClose={() => setReceiveModalOpen(false)}
        receiveDetails={receiveDetails}
        loading={loadingReceiveDetails}
        walletAddress={selectedCryptoBalance?.address}
        network={selectedCryptoBalance?.network}
      />

      <SimpleGrid mb="20px" columns={{ sm: 1, md: 1 }} spacing={{ base: "20px", xl: "20px" }}>
        {loading ? (
          <Flex justifyContent="center" alignItems="center" height="50vh">
            <Spinner size="xl" />
          </Flex>
        ) : (
          <CryptoWalletsTable
            networks={networks}
            sortArray={[1, 5, 10]}
            index={() => { }}
            tableData={cryptoWallets}
            searchQuery={searchQuery}
            onRetrieveBalance={(walletNetwork, walletAddress, onLoadComplete) =>
              getCryptoBalance(walletNetwork, walletAddress, onLoadComplete)
            }
          />
        )}
      </SimpleGrid>
    </Box>
  );

  function handleError(error) {
    constants.showToast(
      "error",
      t("FAILED"),
      error?.message || t("Snap! Something went wrong, Try again")
    );
  }

  function getNetworks() {
    setLoading(true);
    endpoints.getNetworks((error, data) => {
      setLoading(false);
      if (error) {
        handleError(error);
      } else {
        setNetworks(data);
        getFiatWallets();
      }
    });
  }

  function getFiatWallets() {
    setLoading(true);
    endpoints.getFiatWallets((error, data) => {
      setLoading(false);
      if (error) {
        handleError(error);
      } else {
        setFiatWallets(data || []);
        getCryptoMainWallets();
      }
    });
  }

  function getCryptoMainWallets() {
    setLoading(true);
    endpoints.getCryptoMainWallets((error, data) => {
      setLoading(false);
      if (error) {
        handleError(error);
      } else {
        setCryptoBalances(data || []);
        const savedNetwork = initialNetwork.current;
        const savedAddress = initialAddress.current;
        let selectedBalance = null;
        if (savedNetwork && savedAddress && data?.length) {
          selectedBalance = data.find(
            (balance) => balance.network === savedNetwork && balance.address === savedAddress
          );
        }
        if (!selectedBalance) {
          selectedBalance = data?.[0];
        }
        setSelectedCryptoBalance(selectedBalance);
        getCryptoWallets(selectedBalance?.network);
      }
    });
  }

  function getCryptoWallets(currency_short, start_item = 0, end_item = 10, page = 1) {
    setLoading(true);
    endpoints.getCryptoWallets(currency_short, start_item, end_item, page, (error, data) => {
      setLoading(false);
      if (error) {
        handleError(error);
      } else {
        setCryptoWallets(data);
        if (data?.length) {
          data.forEach(wallet => {
            if (wallet.type === 'main_wallet' && (!wallet.balance || wallet.balance === null)) {
              getCryptoBalance(wallet.network, wallet.address, () => { });
            }
          });
        }
      }
    });
  }

  function getCryptoBalance(network, address, onLoadComplete) {
    endpoints.getCryptoBalance(network, address, (error, data) => {
      if (error) {
        handleError(error);
      } else {
        setCryptoWallets(prevWallets =>
          prevWallets.map(wallet =>
            wallet.address === address ? { ...wallet, balance: data } : wallet
          )
        );
        constants.showToast(
          "success",
          t("SUCCESS"),
          t("Balance retrieved successfully!")
        );
      }
      onLoadComplete?.();
    });
  }

  function getCryptoReceiveDetails(network, address) {
    setLoadingReceiveDetails(true);
    setReceiveModalOpen(true);
    endpoints.getCryptoReceiveDetails(network, address, (error, data) => {
      setLoadingReceiveDetails(false);
      if (error) {
        handleError(error);
        setReceiveModalOpen(false);
      } else {
        setReceiveDetails(data);
      }
    });
  }

  function createWallet(postData, onSuccess) {
    if (!postData?.network || !postData?.transfer_network) {
      return constants.showToast("error", t("FAILED"), t("Whoopty Doo! All fields are required"));
    }
    setCreatingWallet(true);
    endpoints.createCryptoWallet(postData, (error, data) => {
      setCreatingWallet(false);
      if (error) {
        handleError(error);
      } else {
        constants.showToast(
          "success",
          t("SUCCESS"),
          t("Crypto wallets created successfully!")
        );
        getNetworks();
        onSuccess?.();
      }
    });
  }

  function getConversionRates(amount, network, currency, transfer_network, onSuccess) {
    if (!amount || !network || !currency) {
      constants.showToast("error", t("FAILED"), t("Whoopty Doo! All fields are required"));
      onSuccess?.(null, new Error("Missing required fields"));
      return;
    }
    endpoints.getCryptoConversionRates(
      'crypto_to_fiat', amount, network, currency, transfer_network, null, null,
      (error, data) => {
        if (error) {
          handleError(error);
          onSuccess?.(null, error);
        } else {
          onSuccess?.(data, null);
        }
      });
  }

  function getNetworkFees(amount, network, debit_currency, from_address, to_address, transfer_network, onSuccess) {
    if (!amount || !network || !debit_currency || !from_address || !to_address) {
      constants.showToast("error", t("FAILED"), t("Whoopty Doo! All fields are required"));
      onSuccess?.(null, new Error("Missing required fields"));
      return;
    }
    endpoints.calculateCryptoNetworkFee(
      amount, network, debit_currency, from_address, to_address, transfer_network,
      (error, data) => {
        if (error) {
          handleError(error);
          onSuccess?.(null, error);
        } else {
          onSuccess?.(data, null);
        }
      });
  }

  function buyCrypto(postData, onSuccess) {
    if (!postData?.amount || !postData?.network || !postData?.debit_currency || !postData?.address) {
      constants.showToast("error", t("FAILED"), t("Whoopty Doo! All fields are required"));
      onSuccess?.(null, new Error("Missing required fields"));
      return;
    }
    endpoints.buyCrypto(postData, (error, data) => {
      if (error) {
        handleError(error);
        onSuccess?.(error, null);
      } else {
        onSuccess?.(null, data);
      }
    });
  }
}